import { computed, onBeforeUnmount, type Ref, ref, watch } from 'vue';
import dayjs from 'dayjs';

// バーを上限まで満たす時間。想定最大より少し長め
const FILL_DURATION_BUFFER_SEC = 30;
// 進捗の上限（100%には到達させず、完了時にスナップさせる）
const CAP_PC = 0.98;
// 完了時に100%へスナップするトランジションの時間
const COMPLETE_TRANSITION_MS = 300;

export const formatDurationLabel = (minSec: number, maxSec: number) => {
  if (minSec >= 60) {
    const toMin = (s: number) => Math.round(s / 60);
    return `${toMin(minSec)}〜${toMin(maxSec)}分`;
  }
  return `${minSec}〜${maxSec}秒`;
};

export const useTimedProgress = (
  source: {
    isGenerating: Ref<boolean>;
    hasError: Ref<boolean>;
    createdAt: Ref<string | undefined>;
    isRestarting?: Ref<boolean>;
  },
  expected: { minSec: number; maxSec: number },
) => {
  const fillDurationMs = (expected.maxSec + FILL_DURATION_BUFFER_SEC) * 1000;

  const progress = ref(0); // 0..1
  const showProgress = ref(false);
  const isComplete = ref(false);

  let rafId: number | undefined;
  let completeTimerId: number | undefined;
  let startTime = 0;

  const progressBarWidth = computed(() => `${progress.value * 100}%`);
  const durationLabel = computed(() =>
    formatDurationLabel(expected.minSec, expected.maxSec),
  );

  const clearTimers = () => {
    if (rafId !== undefined) cancelAnimationFrame(rafId);
    window.clearTimeout(completeTimerId);
    rafId = undefined;
    completeTimerId = undefined;
  };

  const tick = () => {
    const elapsed = performance.now() - startTime;
    if (elapsed >= fillDurationMs) {
      progress.value = CAP_PC;
      return;
    }
    progress.value = (elapsed / fillDurationMs) * CAP_PC;
    rafId = requestAnimationFrame(tick);
  };

  const initialElapsedMs = () => {
    const createdAt = source.createdAt.value;
    if (!createdAt) return 0;
    const elapsed = dayjs().diff(dayjs(createdAt));
    return Math.min(Math.max(elapsed, 0), fillDurationMs);
  };

  const start = () => {
    clearTimers();
    isComplete.value = false;
    showProgress.value = true;
    startTime = performance.now() - initialElapsedMs();
    tick();
  };

  const complete = () => {
    clearTimers();
    isComplete.value = true;
    progress.value = 1;
    completeTimerId = window.setTimeout(() => {
      showProgress.value = false;
    }, COMPLETE_TRANSITION_MS);
  };

  const reset = () => {
    clearTimers();
    progress.value = 0;
    isComplete.value = false;
    showProgress.value = false;
  };

  watch(
    () => source.isGenerating.value,
    (generating, wasGenerating) => {
      if (generating) {
        start();
      } else if (wasGenerating) {
        if (source.hasError.value) {
          reset();
        } else {
          complete();
        }
      }
    },
    { immediate: true },
  );

  watch(
    () => source.isRestarting?.value ?? false,
    (restarting, wasRestarting) => {
      if (restarting && !wasRestarting && source.isGenerating.value) start();
    },
  );

  watch(
    () => source.hasError.value,
    hasError => {
      if (hasError) reset();
    },
  );

  onBeforeUnmount(clearTimers);

  return {
    progress,
    progressBarWidth,
    durationLabel,
    showProgress,
    isComplete,
  };
};
